import localforage from 'localforage';

const STORAGE_KEY = 'defaultCompanyEntity_v1';

// The company entity data is held as a static JSON file. If the data is not present in the local store
// (on the first run, or if the data has been reset), it is reloaded from the file. This means that to 
// change the data, you may need to both change the static data file AND run the "Clear Local Data" 
// page under Admin => Advanced settings.
const loadDefaults = async () => {
  try {
    const defaults = require('./_local/_defaultCompanyEntityData.json');
    return defaults || [];
  } catch (e) {
    return [];
  }
};

const read = async () => {
  try {
    const raw = await localforage.getItem(STORAGE_KEY);
    if (!raw) {
      const defaults = await loadDefaults();
      await localforage.setItem(STORAGE_KEY, defaults);
      return defaults;
    }
    return raw;
  } catch (e) {
    return [];
  }
};

const write = async (data) => {
  await localforage.setItem(STORAGE_KEY, data);
};

// This is called by the "Clear Local Data" page under Admin => Advanced settings
export const resetToDefaults = async () => {
  const defaults = await loadDefaults();
  await write(defaults);
  return defaults;
};

// Get all default company entities. This is used by the Users page when displaying the
// default company entity assignments for users.
export const getAllDefaultCompanyEntities = async () => await read();

// Get a specific default company entity by company ID and entity ID. This is used when
// saving a user to locate the default company entity assignment for that user's company
// and entity. We the add an assignment if one does not already exist.
export const getByCompanyAndEntity = async (companyId, entityId) => {
  if (companyId === null || companyId === undefined || entityId === null || entityId === undefined) return null;
  const data = await read();
  return data.find(d => d && String(d.companyId) === String(companyId) && String(d.entityId) === String(entityId)) || null;
};

// This adds a default company entity assignment to the store. It is used when saving a user
// if the user's default company entity assignment does not already exist.
export const addDefaultCompanyEntity = async (item) => {
  const data = await read();
  data.push(item);
  await write(data);
  console.log("Added default company entity", item);
  return item;
};

// If we're not running locally, we get the new company entity ID from an identity field on the database , but in local 
// mode, we need to simulate it by getting the highest entity ID presently in the store, and incrementing it by 1.
export const getNextEntityId = async () => {
  try {
    const items = await read();
    if (!items || items.length === 0) return 1;
    let max = -Infinity;
    for (const i of items) {
      if (!i) continue;
      const rawId = i.entityId ?? i.id ?? i.entity_id;
      const num = Number(rawId);
      if (!Number.isNaN(num) && isFinite(num)) {
        if (num > max) max = num;
      }
    }
    if (max === -Infinity) return 1;
    return max + 1;
  } catch (e) {
    return 1;
  }
};

export default {
  getAllDefaultCompanyEntities,
  getByCompanyAndEntity,
  addDefaultCompanyEntity,
  getNextEntityId,
  resetToDefaults,
};
